import { apiGet, apiPost } from '@/shared/utils/requestBuilder';
import { assertUrlNavegavelSegura } from '@/shared/utils/urlSegura';
import type {
  ConsultaResponse,
  DownloadResponse,
  VerificacaoArquivoResponse,
} from '../types/autenticacaoPublica.types';

interface ApiSuccessResponse<T> {
  success: boolean;
  data: T;
}

interface ApiErrorResponse {
  error: string;
}

const extractRetryAfter = (response: Response): number | null => {
  const header = response.headers.get('Retry-After');
  return header ? parseInt(header, 10) : null;
};

export class RateLimitException extends Error {
  retryAfterSeconds: number;

  constructor(message: string, retryAfterSeconds: number) {
    super(message);
    this.name = 'RateLimitException';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

// TODO(#838): validar o payload de /autenticar no boundary (Zod) — hoje o tipo
// é afirmado por cast, sem verificação em runtime.
export const autenticacaoPublicaService = {
  consultarCodigo: async (codigo: string): Promise<ConsultaResponse> => {
    const response = await apiGet('/autenticar', { code: codigo }, true);

    if (response.status === 429) {
      const retryAfter = extractRetryAfter(response) ?? 60;
      throw new RateLimitException(
        'Limite de requisições excedido. Tente novamente em instantes.',
        retryAfter,
      );
    }

    if (response.status === 404) {
      const result: ApiSuccessResponse<ConsultaResponse> = await response.json();
      return result.data;
    }

    if (!response.ok) {
      const errorData: ApiErrorResponse = await response
        .json()
        .catch(() => ({ error: 'Erro ao consultar documento' }));
      throw new Error(errorData.error || 'Erro ao consultar documento');
    }

    const result: ApiSuccessResponse<ConsultaResponse> = await response.json();
    return result.data;
  },

  verificarArquivo: async (file: File): Promise<VerificacaoArquivoResponse> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiPost('/autenticar/verificar-arquivo', formData, true);

    if (response.status === 429) {
      const retryAfter = extractRetryAfter(response) ?? 60;
      throw new RateLimitException(
        'Limite de requisições excedido. Tente novamente em instantes.',
        retryAfter,
      );
    }

    if (response.status === 404) {
      const result: ApiSuccessResponse<VerificacaoArquivoResponse> = await response.json();
      return result.data;
    }

    if (!response.ok) {
      const errorData: ApiErrorResponse = await response
        .json()
        .catch(() => ({ error: 'Erro ao verificar arquivo' }));
      throw new Error(errorData.error || 'Erro ao verificar arquivo');
    }

    const result: ApiSuccessResponse<VerificacaoArquivoResponse> = await response.json();
    return result.data;
  },

  downloadDocumento: async (codigo: string, signal?: AbortSignal): Promise<DownloadResponse> => {
    const response = await apiGet('/autenticar/download', { code: codigo }, true, signal);

    if (response.status === 429) {
      const retryAfter = extractRetryAfter(response) ?? 60;
      throw new RateLimitException(
        'Limite de requisições excedido. Tente novamente em instantes.',
        retryAfter,
      );
    }

    if (response.status === 202) {
      const result = await response.json();
      return { status: 'GENERATING', message: result.data?.message || 'PDF em geração' };
    }

    if (!response.ok) {
      const errorData: ApiErrorResponse = await response
        .json()
        .catch(() => ({ error: 'Documento não disponível para download' }));
      throw new Error(errorData.error || 'Documento não disponível para download');
    }

    const result: ApiSuccessResponse<{ redirectUrl?: string; status?: string; message?: string }> =
      await response.json();

    if (result.data?.status === 'GENERATING') {
      return { status: 'GENERATING', message: result.data.message || 'PDF em geração' };
    }

    if (result.data?.redirectUrl) {
      return {
        redirectUrl: assertUrlNavegavelSegura(result.data.redirectUrl, 'Link do documento'),
      };
    }

    throw new Error('Documento não disponível para download');
  },
};
